import { Utils } from '@everyboard/lib';
import { Player } from '../../../jscaip/Player';
import { PlayerNumberMap } from '../../../jscaip/PlayerMap';
import { GameStateWithTable } from '../../../jscaip/state/GameStateWithTable';
export class CheckersPiece {
    player;
    isPromoted;
    static ZERO = new CheckersPiece(Player.ZERO, false);
    static ONE = new CheckersPiece(Player.ONE, false);
    static ZERO_PROMOTED = new CheckersPiece(Player.ZERO, true);
    static ONE_PROMOTED = new CheckersPiece(Player.ONE, true);
    constructor(player, isPromoted) {
        this.player = player;
        this.isPromoted = isPromoted;
    }
    toString() {
        switch (this) {
            case CheckersPiece.ZERO: return 'u';
            case CheckersPiece.ONE: return 'v';
            case CheckersPiece.ZERO_PROMOTED: return 'O';
            default:
                Utils.expectToBe(this, CheckersPiece.ONE_PROMOTED);
                return 'X';
        }
    }
    equals(other) {
        return this === other;
    }
    /**
     * Returns a promoted version of this piece
     */
    promote() {
        if (this.player === Player.ZERO) {
            return CheckersPiece.ZERO_PROMOTED;
        }
        else {
            return CheckersPiece.ONE_PROMOTED;
        }
    }
}
export class CheckersStack {
    pieces;
    static EMPTY = new CheckersStack([]);
    // The list of pieces is from top to bottom, hence [commander, its allies, its prisoners, more prisoners]
    constructor(pieces) {
        this.pieces = pieces;
    }
    isEmpty() {
        return this.pieces.length === 0;
    }
    isOccupied() {
        return this.pieces.length > 0;
    }
    isCommandedBy(player) {
        if (this.isEmpty()) {
            return false;
        }
        return this.getCommander().player === player;
    }
    getCommander() {
        return this.pieces[0];
    }
    getPiecesUnderCommander() {
        return new CheckersStack(this.pieces.slice(1));
    }
    capturePiece(piece) {
        return new CheckersStack(this.pieces.concat(piece));
    }
    addStackBelow(stack) {
        return new CheckersStack(this.pieces.concat(stack.pieces));
    }
    getStackSize() {
        return this.pieces.length;
    }
    promoteCommander() {
        let commander = this.getCommander();
        if (commander.isPromoted) {
            return this;
        }
        else {
            commander = commander.promote();
            const remainingStack = this.getPiecesUnderCommander();
            const commandingStack = new CheckersStack([commander]);
            return commandingStack.addStackBelow(remainingStack);
        }
    }
    get(index) {
        return this.pieces[index];
    }
    toString(length) {
        let leftFill = length - this.getStackSize();
        let result = '';
        while (leftFill > 0) {
            result += '_';
            leftFill--;
        }
        for (const piece of this.pieces) {
            result += piece.toString();
        }
        return result;
    }
}
export class CheckersState extends GameStateWithTable {
    static SIZE = 7;
    constructor(board, turn) {
        // Constructor is protected so that we use Even/OddCheckersState instead to check the validity at construction
        super(board, turn);
    }
    getStacksOf(player) {
        const stackCoords = [];
        for (const coordAndContent of this.getCoordsAndContents()) {
            if (coordAndContent.content.isCommandedBy(player)) {
                stackCoords.push(coordAndContent.coord);
            }
        }
        return stackCoords;
    }
    set(coord, value) {
        const newBoard = this.getCopiedBoard();
        newBoard[coord.y][coord.x] = value;
        return new CheckersState(newBoard, this.turn);
    }
    remove(coord) {
        return this.set(coord, CheckersStack.EMPTY);
    }
    incrementTurn() {
        return new CheckersState(this.getCopiedBoard(), this.turn + 1);
    }
    getFinishLineOf(player) {
        if (player === Player.ZERO) {
            return 0;
        }
        else {
            return this.getHeight() - 1;
        }
    }
    coordIsCommandedBy(coord, player) {
        const optional = this.getOptionalPieceAt(coord);
        if (optional.isPresent()) {
            return optional.get().isCommandedBy(player);
        }
        else {
            return false;
        }
    }
    isEmptyAt(coord) {
        const optional = this.getOptionalPieceAt(coord);
        if (optional.isPresent()) {
            return optional.get().isEmpty();
        }
        else {
            return false;
        }
    }
    getScores() {
        const zeroScore = this.getStacksOf(Player.ZERO).length;
        const oneScore = this.getStacksOf(Player.ONE).length;
        return PlayerNumberMap.of(zeroScore, oneScore);
    }
    toString() {
        let biggerStack = 1;
        const height = this.getHeight();
        const width = this.getWidth();
        for (let y = 0; y < height; y++) {
            for (let x = 0; x < width; x++) {
                const newStackSize = this.getPieceAtXY(x, y).getStackSize();
                biggerStack = Math.max(biggerStack, newStackSize);
            }
        }
        const lines = [];
        for (let y = 0; y < height; y++) {
            const squares = [];
            for (let x = 0; x < width; x++) {
                squares.push(this.getPieceAtXY(x, y).toString(biggerStack));
            }
            lines.push(squares.join(' '));
        }
        return lines.join('\n');
    }
}
/*
 * An "even" checkers state is a state where there are pieces are on even-numbered squares.
 * That is, in the starting position, square (0, 0) contain a piece while (1, 0) and (0, 1) are empty.
 */
export class EvenCheckersState extends CheckersState {
    static of(board, turn) {
        const state = new CheckersState(board, turn);
        state.forEachCoord((coord, content) => {
            if ((coord.x + coord.y) % 2 === 1) {
                Utils.assert(content.isEmpty(), `Invalid even checkers state contains a piece at (${coord.x}, ${coord.y})`);
            }
        });
        return state;
    }
}
/*
 * An "odd" checkers state is a state where there pieces are on odd-numbered squares.
 * That is, in the starting position, square (0, 0) is empty while (1, 0) and (0, 1) contain a piece.
 */
export class OddCheckersState extends CheckersState {
    static of(board, turn) {
        const state = new CheckersState(board, turn);
        state.forEachCoord((coord, content) => {
            if ((coord.x + coord.y) % 2 === 0) {
                Utils.assert(content.isEmpty(), `Invalid odd checkers state contains a piece at (${coord.x}, ${coord.y})`);
            }
        });
        return state;
    }
}
